import {
  ContractorType,
  ContractorTypeDisplayName,
  ContractorTypeEnum,
} from "pmt-types/src/enums/ContractorType.ts";
import {
  EnrolleeStatus,
  EnrolleeStatusDisplayName,
  EnrolleeStatusEnum,
} from "pmt-types/src/enums/EnrolleeStatus.ts";
import {
  LiabilityRatePer,
  LiabilityRatePerDisplayName,
} from "pmt-types/src/enums/LiabilityRatePer.ts";
import {
  LiabilityRateType,
  LiabilityRateTypeDisplayName,
} from "pmt-types/src/enums/LiabilityRateType.ts";
import { UnitedStates } from "pmt-types/src/enums/UnitedStates.ts";
import { Enrollee, EnrolleeSchema } from "pmt-types/src/models/Enrollee.ts";
import { useQueryClient } from "react-query";
import { useNavigate, useParams } from "react-router-dom";
import EntityAsyncSelect from "../../components/form/entityAsyncSelect.tsx";
import EnumSelect from "../../components/form/enumSelect.tsx";
import FormInput from "../../components/form/formInput.tsx";
import NumberInput from "../../components/form/numberInput.tsx";
import PmtForm from "../../components/form/pmtForm.tsx";
import SearchableSelect from "../../components/form/searchableSelect.tsx";
import FormRoot from "../../components/form/style/formRoot.tsx";
import FormSection from "../../components/form/style/formSection.tsx";
import TabbedSelect from "../../components/form/tabbedSelect.tsx";
import TextArea from "../../components/form/textArea.tsx";
import LoadingSpinner from "../../components/loadingSpinner.tsx";
import NotFoundPage from "../../global/notFoundPage.tsx";
import { useMutationSearchContractors } from "../../hooks/mutations/useMutationSearchContractors.ts";
import { useMutationSearchProjects } from "../../hooks/mutations/useMutationSearchProjects.ts";
import { useMutationSearchTypeOfWorks } from "../../hooks/mutations/useMutationSearchTypeOfWorks.ts";
import { useMutationUpsertEnrollee } from "../../hooks/mutations/useMutationUpsertEnrollee.ts";
import { QueryKeys } from "../../hooks/queries/queryKeys.ts";
import { useQueryGetEnrolleeById } from "../../hooks/queries/useQueryGetEnrolleeById.ts";
import { useQueryGetProjectById } from "../../hooks/queries/useQueryGetProjectById.ts";
import { useQueryGetSourceById } from "../../hooks/queries/useQueryGetSourceById.ts";
import useQueryParams from "../../hooks/useQueryParams.ts";
import enumToLabelValue from "../../utils/transforms/enumToLabelValue.ts";
import { transformSelectInput } from "../../utils/transforms/transformSelectInput.ts";

type Props = {
  requiresProjectSelection: boolean;
};

export default function EditEnrollee({ requiresProjectSelection }: Props) {
  const navigate = useNavigate();
  const { sourceId, projectId, enrolleeId } = useParams();
  const queryParams = useQueryParams();
  const queryClient = useQueryClient();

  const { data: project, isFetching: isProjectFetching } =
    useQueryGetProjectById(sourceId, projectId);
  const { data: source, isFetching: isSourceFetching } = useQueryGetSourceById(
    project?.source.id,
  );

  const { mutateAsync: searchProjects } = useMutationSearchProjects();
  const { mutateAsync: searchTypeOfWorks } = useMutationSearchTypeOfWorks();
  const { mutateAsync: searchContractors } = useMutationSearchContractors();
  const { mutate, isLoading: isSubmitting } = useMutationUpsertEnrollee();

  const { data: enrollee, isFetching } = useQueryGetEnrolleeById(
    sourceId,
    projectId,
    enrolleeId,
  );

  if (!requiresProjectSelection && enrolleeId == null) {
    return <NotFoundPage />;
  }

  if (
    isFetching ||
    (!requiresProjectSelection && (isProjectFetching || isSourceFetching))
  ) {
    return <LoadingSpinner />;
  }

  const isNew = enrolleeId === "new" || requiresProjectSelection;

  if (
    (!isNew && enrollee == null) ||
    (!requiresProjectSelection && (source == null || project == null))
  ) {
    return <NotFoundPage />;
  }

  const defaultEnrolleeValues: Partial<Enrollee> =
    source != null && project != null
      ? {
          id: "new",
          source: {
            id: source.id,
            name: source.name,
          },
          project: {
            id: project.id,
            name: project.name,
          },
        }
      : {
          id: "new",
        };

  const navigateAfterSave = (enrollee: Enrollee) => {
    if (queryParams.get("return") === "project") {
      navigate(
        `/sources/${enrollee.source.id}/projects/${enrollee.project.id}/details`,
      );
    } else if (queryParams.get("return") === "search") {
      navigate(`/enrollees/`);
    } else {
      navigate(
        `/sources/${enrollee.source.id}/projects/${enrollee.project.id}/enrollees/${enrollee.id}/details`,
      );
    }
  };

  const pageTitle =
    source != null && project != null
      ? `${source.name} / ${project.name} / ${
          isNew ? "New Enrollee" : enrollee?.name ?? "Edit Enrollee"
        }`
      : "New Enrollee";

  return (
    <PmtForm<Enrollee>
      schema={EnrolleeSchema}
      pageTitle={pageTitle}
      defaultValues={enrollee ?? defaultEnrolleeValues}
      submitMutation={mutate}
      onSave={async (enrollee: Enrollee) => {
        await queryClient.invalidateQueries({
          queryKey: [QueryKeys.GetEnrollees, enrollee.id],
        });
        await queryClient.invalidateQueries({
          queryKey: [QueryKeys.GetProjectEnrollees, enrollee.project.id],
        });
        navigateAfterSave(enrollee);
      }}
      onCancel={() => navigate(-1)}
      isSubmitting={isSubmitting}
      renderForm={(form) => {
        form.register("source");
        form.register("project");
        return (
          <FormRoot>
            {requiresProjectSelection ? (
              <FormSection label={"Project"}>
                <EntityAsyncSelect
                  name="project"
                  label="Source / Project"
                  placeholder="Search by project name..."
                  initialValue={
                    enrollee && {
                      source: enrollee.source,
                      project: enrollee.project,
                    }
                  }
                  renderLabel={(value) => {
                    if (value?.source == null || value?.project == null) {
                      return "";
                    }

                    return `${value.source.name} / ${value.project.name}`;
                  }}
                  queryFn={async (filter: string) => {
                    const projects = await searchProjects(filter);

                    return projects.map((project) => ({
                      project: {
                        name: project.name,
                        id: project.id,
                      },
                      source: {
                        name: project.source.name,
                        id: project.source.id,
                      },
                    }));
                  }}
                  onChange={(value) => {
                    if (value == null) {
                      return;
                    }

                    form.setValue("source", value.source);
                    form.setValue("project", value.project);
                  }}
                />
              </FormSection>
            ) : (
              <></>
            )}
            <FormSection label={"Details"}>
              <FormInput name={"name"} label={"Name"} />
              <EnumSelect
                name="status"
                label="Status"
                data={EnrolleeStatus.options}
                formatOption={(option) =>
                  EnrolleeStatusDisplayName[option as EnrolleeStatusEnum]
                }
              />
              <FormInput name="emailAddress" label="Email Address" />
              <EnumSelect
                name="contractorType"
                label="Tier"
                data={ContractorType.options}
                formatOption={(option) =>
                  ContractorTypeDisplayName[option as ContractorTypeEnum]
                }
              />
              <SearchableSelect
                name="typeOfWork"
                label="Type of Work"
                transform={transformSelectInput}
                queryFn={searchTypeOfWorks}
              />
            </FormSection>
            <FormSection label={"Address"}>
              <FormInput name="address.street" label="Street" />
              <FormInput name="address.city" label="City" />
              <EnumSelect
                name="address.state"
                label="State"
                data={UnitedStates.options}
              />
              <FormInput name="address.zipCode" label="Zip Code" />
            </FormSection>
            <FormSection label={"Project Details"}>
              <FormInput name={"contractNumber"} label={"Contract Number"} />
              <SearchableSelect
                name="awardingContractor"
                label="Awarding Contractor"
                transform={transformSelectInput}
                queryFn={searchContractors}
              />
              <FormInput name="awardDate" label="Award Date" type="date" />
              <FormInput
                name="expirationDate"
                label="Expiration Date"
                type="date"
              />
              <NumberInput
                name="estimatedContractValue"
                label="Estimated Contract Value"
                prefix="$"
                min={0}
                step={10000}
              />
              <NumberInput
                name="estimatedSitePayroll"
                label="Estimated Site Payroll"
                prefix="$"
                min={0}
                step={10000}
              />
              <FormInput name="fein" label="FEIN/Tax ID" />
            </FormSection>
            <FormSection label={"Coverage Details"}>
              <div className="col-span-full flex flex-col">
                <NumberInput
                  name="liabilityRate"
                  label="Liability Rate"
                  prefix="$"
                  min={0}
                  step={10000}
                  decimalsLimit={4}
                />
                <TabbedSelect
                  name="liabilityRateType"
                  tabs={enumToLabelValue(
                    LiabilityRateType.enum,
                    LiabilityRateTypeDisplayName,
                  )}
                />
                <TabbedSelect
                  name="liabilityRatePer"
                  tabs={enumToLabelValue(
                    LiabilityRatePer.enum,
                    LiabilityRatePerDisplayName,
                  )}
                />
              </div>
              <NumberInput
                name="policyDeductible"
                label="Policy Deductible"
                prefix="$"
                min={0}
                step={10000}
              />
              <NumberInput
                name="selfInsuredRetention"
                label="Self-Insured Retention"
                prefix="$"
                min={0}
                step={10000}
              />
              <NumberInput
                name="bidDeductPercentage"
                label="Bid Deduct Percentage"
                prefix="%"
                min={0}
                max={100}
                step={1}
                decimalsLimit={4}
              />
            </FormSection>
            <FormSection>
              <TextArea name="notes" label="Notes" />
            </FormSection>
          </FormRoot>
        );
      }}
    />
  );
}
